/**
 * Procedural hull geometry.
 *
 * Hulls are lofted from a handful of cross-sections along the keel line. Each
 * section is a closed loop — up one side, across the deck, down the other, back
 * along the bottom — so stitching consecutive sections produces a watertight
 * solid with no separate deck or transom to fit.
 *
 * These are the only functions that know what a boat looks like. Everything
 * downstream works off the physics body and the named attachment points, so
 * replacing a loft with a loaded glTF is a local change: return a Group with
 * the same origin (at the resting waterline, +Z forward) and you are done.
 */

import {
  BufferAttribute,
  BufferGeometry,
  BoxGeometry,
  CylinderGeometry,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three/webgpu'

export interface Station {
  /** Position along the keel. Negative is aft. */
  z: number
  /** Half-beam at the waterline. */
  halfBeam: number
  /** Depth of the keel below the origin. */
  depth: number
  /** Height of the deck edge above the origin. */
  deckY: number
}

/** Points per cross-section, counted around the closed loop. */
const SECTION_POINTS = 14

/**
 * Build one closed cross-section, starting at the keel and running clockwise
 * when viewed from ahead: down the starboard bilge, up to the deck edge, across
 * the deck, then back down the port side.
 */
function section(station: Station, out: number[]): void {
  const { z, halfBeam: b, depth: d, deckY } = station

  // Starboard profile, keel to deck edge. The bilge radius is what stops the
  // hull reading as an extruded rectangle.
  const profile: [number, number][] = [
    [0, -d],
    [b * 0.5, -d * 0.94],
    [b * 0.85, -d * 0.6],
    [b * 0.99, -d * 0.2],
    [b, deckY * 0.35],
    [b * 0.95, deckY],
  ]

  for (const [x, y] of profile) out.push(x, y, z)
  // Deck crown, slightly proud amidships so water runs off.
  out.push(0, deckY * 1.06, z)
  for (let i = profile.length - 1; i >= 0; i--) {
    const [x, y] = profile[i]
    out.push(-x, y, z)
  }
  out.push(0, -d, z)
}

/**
 * Loft a closed tube through a list of sections and cap both ends.
 * Sections must be ordered from aft to forward.
 */
export function loftHull(stations: readonly Station[]): BufferGeometry {
  const positions: number[] = []
  for (const station of stations) section(station, positions)

  const sectionCount = stations.length
  const indices: number[] = []

  for (let s = 0; s < sectionCount - 1; s++) {
    const a = s * SECTION_POINTS
    const b = (s + 1) * SECTION_POINTS
    for (let i = 0; i < SECTION_POINTS; i++) {
      const iNext = (i + 1) % SECTION_POINTS
      indices.push(a + i, b + i, b + iNext)
      indices.push(a + i, b + iNext, a + iNext)
    }
  }

  // End caps: a fan to a centroid vertex at each extremity.
  const capCentroid = (sectionIndex: number): number => {
    const base = sectionIndex * SECTION_POINTS
    let y = 0
    for (let i = 0; i < SECTION_POINTS; i++) y += positions[(base + i) * 3 + 1]
    positions.push(0, y / SECTION_POINTS, stations[sectionIndex].z)
    return positions.length / 3 - 1
  }

  const sternCentre = capCentroid(0)
  for (let i = 0; i < SECTION_POINTS; i++) {
    indices.push(sternCentre, ((i + 1) % SECTION_POINTS), i)
  }

  const bowSection = sectionCount - 1
  const bowCentre = capCentroid(bowSection)
  const bowBase = bowSection * SECTION_POINTS
  for (let i = 0; i < SECTION_POINTS; i++) {
    indices.push(bowCentre, bowBase + i, bowBase + ((i + 1) % SECTION_POINTS))
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3))
  geometry.setIndex(indices)
  geometry.computeVertexNormals()
  return geometry
}

function material(hex: string, roughness = 0.75, metalness = 0.05): MeshStandardNodeMaterial {
  const m = new MeshStandardNodeMaterial({ color: new Color(hex) })
  m.roughness = roughness
  m.metalness = metalness
  return m
}

function mesh(geometry: BufferGeometry, mat: MeshStandardNodeMaterial): Mesh {
  const m = new Mesh(geometry, mat)
  m.castShadow = true
  m.receiveShadow = true
  return m
}

/** Where a rope, a light, or an exhaust plume attaches to a vessel. */
export interface HullAttachments {
  /** Towing bollard, aft. Ropes pull from here. */
  towPoint: Vector3
  /** Wheelhouse eye height, for the bridge camera. */
  bridge: Vector3
  /** Exhaust outlet, for the funnel smoke. */
  exhaust: Vector3
  /** Propeller wash origin, at the stern below the waterline. */
  propeller: Vector3
}

const TUG_STATIONS: Station[] = [
  { z: -9.0, halfBeam: 2.75, depth: 1.7, deckY: 1.35 },
  { z: -6.5, halfBeam: 3.4, depth: 2.35, deckY: 1.4 },
  { z: -2.0, halfBeam: 3.55, depth: 2.55, deckY: 1.5 },
  { z: 2.0, halfBeam: 3.45, depth: 2.45, deckY: 1.75 },
  { z: 5.5, halfBeam: 2.8, depth: 1.95, deckY: 2.15 },
  { z: 7.8, halfBeam: 1.6, depth: 1.2, deckY: 2.5 },
  { z: 9.0, halfBeam: 0.3, depth: 0.55, deckY: 2.85 },
]

/**
 * A harbour tug: 18 m, low aft working deck, tall wheelhouse forward, fenders
 * everywhere because its whole job is leaning on other people's paint.
 */
export function buildTugHull(): { group: Group; attachments: HullAttachments } {
  const group = new Group()
  group.name = 'tug-hull'

  const hullMat = material('#8f2f28', 0.62)
  const bootTopMat = material('#161a1d', 0.55)
  const deckMat = material('#4a5560', 0.9)
  const houseMat = material('#e6e3dc', 0.7)
  const trimMat = material('#1d2429', 0.5, 0.25)
  const brassMat = material('#c9a227', 0.35, 0.7)
  const rubberMat = material('#22262a', 0.95)

  group.add(mesh(loftHull(TUG_STATIONS), hullMat))

  // Boot topping: a dark band straddling the waterline, scaled a hair proud of
  // the hull so it reads as paint rather than z-fighting.
  const boot = loftHull(
    TUG_STATIONS.map((s) => ({ ...s, halfBeam: s.halfBeam * 1.004, depth: s.depth * 0.42, deckY: 0.34 })),
  )
  group.add(mesh(boot, bootTopMat))

  // Aft working deck.
  const deck = mesh(new BoxGeometry(6.4, 0.18, 9.5), deckMat)
  deck.position.set(0, 1.5, -3.6)
  group.add(deck)

  // Bulwark rail around the working deck.
  for (const side of [-1, 1]) {
    const rail = mesh(new BoxGeometry(0.16, 0.72, 9.5), trimMat)
    rail.position.set(side * 3.25, 1.9, -3.6)
    group.add(rail)
  }
  const transomRail = mesh(new BoxGeometry(6.5, 0.72, 0.16), trimMat)
  transomRail.position.set(0, 1.9, -8.3)
  group.add(transomRail)

  // Deckhouse and wheelhouse, stepped.
  const house = mesh(new BoxGeometry(5.4, 2.5, 6.2), houseMat)
  house.position.set(0, 2.9, 2.4)
  group.add(house)

  const wheelhouse = mesh(new BoxGeometry(4.2, 2.2, 4.0), houseMat)
  wheelhouse.position.set(0, 5.2, 2.0)
  group.add(wheelhouse)

  // Window band. One flat box per face is plenty at the range you view it from.
  const glassMat = material('#16323f', 0.15, 0.6)
  const windowFront = mesh(new BoxGeometry(4.24, 1.1, 0.08), glassMat)
  windowFront.position.set(0, 5.5, 4.0)
  group.add(windowFront)
  for (const side of [-1, 1]) {
    const w = mesh(new BoxGeometry(0.08, 1.1, 3.6), glassMat)
    w.position.set(side * 2.11, 5.5, 2.0)
    group.add(w)
  }

  const roof = mesh(new BoxGeometry(4.5, 0.16, 4.3), trimMat)
  roof.position.set(0, 6.38, 2.0)
  group.add(roof)

  // Funnel, raked slightly aft the way they usually are.
  const funnel = mesh(new CylinderGeometry(0.62, 0.75, 2.6, 16), trimMat)
  funnel.position.set(0, 5.3, -0.6)
  funnel.rotation.x = -0.06
  group.add(funnel)
  const funnelBand = mesh(new CylinderGeometry(0.66, 0.66, 0.5, 16), hullMat)
  funnelBand.position.set(0, 6.1, -0.65)
  group.add(funnelBand)

  // Mast and navigation lights.
  const mast = mesh(new CylinderGeometry(0.09, 0.11, 3.2, 8), trimMat)
  mast.position.set(0, 7.9, 1.6)
  group.add(mast)

  const lightMat = material('#ffffff', 0.3)
  lightMat.emissive = new Color('#fff4d0')
  lightMat.emissiveIntensity = 2
  const mastLight = mesh(new SphereGeometry(0.16, 10, 8), lightMat)
  mastLight.position.set(0, 9.5, 1.6)
  group.add(mastLight)

  // Towing winch on the aft deck, and the bollard the hawser actually leads to.
  const winchDrum = mesh(new CylinderGeometry(0.85, 0.85, 2.6, 18), trimMat)
  winchDrum.rotation.z = Math.PI / 2
  winchDrum.position.set(0, 2.35, -1.3)
  group.add(winchDrum)
  for (const side of [-1, 1]) {
    const cheek = mesh(new CylinderGeometry(1.15, 1.15, 0.18, 18), brassMat)
    cheek.rotation.z = Math.PI / 2
    cheek.position.set(side * 1.4, 2.35, -1.3)
    group.add(cheek)
  }

  const towBollard = mesh(new CylinderGeometry(0.3, 0.34, 1.0, 12), brassMat)
  towBollard.position.set(0, 2.0, -6.4)
  group.add(towBollard)

  // Fenders: a belt of tyres round the working part of the hull, plus the big
  // cylindrical bow fender a tug pushes with.
  for (const side of [-1, 1]) {
    for (let i = 0; i < 6; i++) {
      const z = -8.0 + i * 2.6
      const tyre = mesh(new TorusGeometry(0.55, 0.2, 8, 14), rubberMat)
      tyre.rotation.y = Math.PI / 2
      tyre.position.set(side * 3.5, 0.75, z)
      group.add(tyre)
    }
  }
  const bowFender = mesh(new CylinderGeometry(0.72, 0.72, 2.6, 14), rubberMat)
  bowFender.rotation.z = Math.PI / 2
  bowFender.position.set(0, 1.5, 8.6)
  group.add(bowFender)

  return {
    group,
    attachments: {
      towPoint: new Vector3(0, 2.5, -6.4),
      bridge: new Vector3(0, 6.0, 2.2),
      exhaust: new Vector3(0, 6.6, -0.7),
      propeller: new Vector3(0, -1.4, -8.6),
    },
  }
}

// Freeboard matters more than it looks like it should: with the deck edge only
// a metre off the water the swell laps over it and the whole barge reads as
// sinking. 2.4 m keeps green water off the deck in the default sea state.
//
// 220 m x 28 m: ship-sized, but the containers stay at their true 12 m size —
// scale comes from more of them, not bigger ones, so the tug still reads as
// tug-sized alongside.
const BARGE_STATIONS: Station[] = [
  { z: -108.0, halfBeam: 12.0, depth: 2.5, deckY: 2.3 },
  { z: -95.0, halfBeam: 14.0, depth: 3.0, deckY: 2.4 },
  { z: 75.0, halfBeam: 14.0, depth: 3.05, deckY: 2.4 },
  { z: 92.0, halfBeam: 12.5, depth: 2.5, deckY: 2.55 },
  { z: 103.0, halfBeam: 7.5, depth: 1.4, deckY: 2.75 },
  { z: 108.0, halfBeam: 2.2, depth: 0.7, deckY: 2.95 },
]

const CARGO_COLOURS = ['#b5451f', '#2f6ea6', '#3f7a3a', '#c08a1e', '#7a4a86', '#a83a3a']

/**
 * A deck cargo barge: 220 m, blunt, and utterly indifferent to where you would
 * like it to go. Containers are stacked with a deterministic pattern so the
 * silhouette is interesting without being different every reload.
 */
export function buildBargeHull(seed = 1): { group: Group; attachments: HullAttachments } {
  const group = new Group()
  group.name = 'barge-hull'

  const hullMat = material('#3d4a52', 0.8)
  // The fast bow taper flips the winding of the loft's crown faces, which
  // backface-culls them from above — the "water through the bow" bug. One
  // double-sided hull costs nothing at this triangle count.
  hullMat.side = DoubleSide
  const deckMat = material('#5a6169', 0.92)
  const trimMat = material('#d8a41c', 0.6)

  group.add(mesh(loftHull(BARGE_STATIONS), hullMat))

  const deck = mesh(new BoxGeometry(26.0, 0.2, 200), deckMat)
  deck.position.set(0, 2.4, -4)
  group.add(deck)

  // Coaming around the cargo area.
  for (const side of [-1, 1]) {
    const coaming = mesh(new BoxGeometry(0.3, 0.9, 200), trimMat)
    coaming.position.set(side * 12.9, 2.8, -4)
    group.add(coaming)
  }

  // Containers, 12 m x 2.4 m, in a deterministic pseudo-random stack.
  let rng = seed * 9301 + 49297
  const next = () => {
    rng = (rng * 9301 + 49297) % 233280
    return rng / 233280
  }
  const containerMats = CARGO_COLOURS.map((c) => material(c, 0.85))

  // 5 rows x 15 bays, stacks up to 3 high. The occasional empty slot keeps
  // the block from reading as one extruded shape.
  for (let row = 0; row < 5; row++) {
    for (let bay = 0; bay < 15; bay++) {
      if (next() < 0.08) continue
      const height = 1 + Math.floor(next() * 2.9)
      for (let level = 0; level < height; level++) {
        const box = mesh(new BoxGeometry(4.6, 2.5, 11.6), containerMats[Math.floor(next() * containerMats.length)])
        box.position.set((row - 2) * 4.85, 2.5 + 1.3 + level * 2.55, -97 + bay * 12.2)
        box.rotation.y = (next() - 0.5) * 0.01
        group.add(box)
      }
    }
  }

  // Towing bitts at the bow, and a stern bitt for the next leg.
  const bittMat = material('#c9a227', 0.4, 0.7)
  for (const side of [-1, 1]) {
    const bitt = mesh(new CylinderGeometry(0.32, 0.36, 1.1, 12), bittMat)
    bitt.position.set(side * 1.6, 3.2, 103)
    group.add(bitt)
  }

  return {
    group,
    attachments: {
      towPoint: new Vector3(0, 3.3, 106),
      bridge: new Vector3(0, 4.0, 0),
      exhaust: new Vector3(0, 2, -90),
      propeller: new Vector3(0, -1, -107),
    },
  }
}
